import { Button, Flex, Space, Typography, message } from 'antd'
import { marked } from 'marked'
import { MixpanelClient } from '~/plugins/mixpanel/client'

const PLATFORMS = [
  'Wix',
  'WordPress',
  'Squarespace',
  'Markdown',
  'HTML',
] as const
export type Platform = (typeof PLATFORMS)[number]

const extractImageUrls = (markdown: string): string[] => {
  const regex = /!\[.*?\]\((.*?)\)/g
  const urls: string[] = []
  let match
  while ((match = regex.exec(markdown)) !== null) {
    urls.push(match[1])
  }
  return urls
}

type Props = {
  content: string
  platform: Platform
  onBack: () => void
  onPublish: (platform: Platform) => void
}

export const GenericCopyFlow: React.FC<Props> = ({
  content,
  platform,
  onBack,
  onPublish,
}) => {
  const mixpanel = MixpanelClient.use()

  const getExport = async (): Promise<string> => {
    if (platform === 'HTML') {
      return marked.parse(content)
    }
    if (platform === 'Markdown') {
      return content
    }
    return marked.parse(content)
  }

  const handleCopy = async () => {
    if (platform === 'Wix') {
      const html = await marked.parse(content)

      const htmlWithSpacing = html
        .replace(/<\/p>/g, '</p><p>&nbsp;</p>')
        .replace(/<\/ul>/g, '</ul><p>&nbsp;</p>')
        .replace(/<\/h[1-6]>/g, match => `${match}<p>&nbsp;</p>`)

      const listener = (e: ClipboardEvent) => {
        e.preventDefault()
        e.clipboardData?.setData('text/html', htmlWithSpacing)
        e.clipboardData?.setData('text/plain', content)
      }

      document.addEventListener('copy', listener)

      try {
        const success = document.execCommand('copy')
        if (success) {
          message.success('Wix content copied')
          mixpanel.track(`sales.publish.${platform}`)
          onPublish(platform)
        } else {
          throw new Error('execCommand failed')
        }
      } catch (err) {
        message.error('Failed to copy content for Wix')
        mixpanel.track(`sales.publish.${platform}.failed`)
      } finally {
        document.removeEventListener('copy', listener)
      }

      return
    }

    try {
      const data = await getExport()
      await navigator.clipboard.writeText(data)
      onPublish(platform)
      message.success(`${platform} content copied`)
      mixpanel.track(`sales.publish.${platform}`)
    } catch (err) {
      message.error('Failed to copy content')
      mixpanel.track(`sales.publish.${platform}.failed`)
    }
  }

  const renderInstructions = () => {
    const images = extractImageUrls(content)

    switch (platform) {
      case 'WordPress':
        return null
      case 'Wix':
        return (
          <>
            <Typography.Paragraph>
              1. Click the <strong>"Copy for Wix"</strong> button below to copy
              your content.
            </Typography.Paragraph>
            <Typography.Paragraph>
              2. In Wix, add a <strong>text block</strong> and paste the copied
              content inside.
            </Typography.Paragraph>
            <Typography.Paragraph>
              3. Move the blog title manually into the{' '}
              <strong>"Add Title"</strong> field.
            </Typography.Paragraph>
            {images.length > 0 && (
              <>
                <Typography.Paragraph>
                  4. For each image below:
                  <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                    <li>Click “Copy” next to the image.</li>
                    <li>In Wix: Add → Media → Image → Upload Media → Link.</li>
                    <li>Paste the link and confirm.</li>
                    <li>
                      Drag the image into the right place in your text block.
                    </li>
                  </ul>
                </Typography.Paragraph>
                <Typography.Title level={5}>Image Links</Typography.Title>
                <div
                  style={{ maxHeight: 200, overflowY: 'auto', paddingRight: 4 }}
                >
                  {images.map((url, index) => (
                    <Flex
                      key={index}
                      align="center"
                      justify="space-between"
                      style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        padding: 8,
                        marginBottom: 8,
                        gap: 12,
                      }}
                    >
                      <Flex align="center" gap={12}>
                        <img
                          src={url}
                          alt={`Image ${index + 1}`}
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                        <Typography.Text
                          strong
                        >{`Image ${index + 1}`}</Typography.Text>
                      </Flex>
                      <Button
                        size="small"
                        onClick={async () => {
                          await navigator.clipboard.writeText(url)
                          message.success('Image link copied')
                        }}
                      >
                        Copy
                      </Button>
                    </Flex>
                  ))}
                </div>
              </>
            )}
          </>
        )
      case 'Squarespace':
        return (
          <>
            <Typography.Paragraph>
              1. Open the Squarespace editor and paste the content.
            </Typography.Paragraph>
            {images.length > 0 && (
              <>
                <Typography.Paragraph>
                  2. For each image below:
                  <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                    <li>Click “Open” to open the image in a new tab.</li>
                    <li>Right-click the image and save it locally.</li>
                    <li>
                      Use the "+" button in Squarespace to add an image block.
                    </li>
                  </ul>
                </Typography.Paragraph>
                <Typography.Title level={5}>
                  Images to Download
                </Typography.Title>
                <div
                  style={{ maxHeight: 200, overflowY: 'auto', paddingRight: 4 }}
                >
                  {images.map((url, index) => (
                    <Flex
                      key={index}
                      align="center"
                      justify="space-between"
                      style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        padding: 8,
                        marginBottom: 8,
                        gap: 12,
                      }}
                    >
                      <Flex align="center" gap={12}>
                        <img
                          src={url}
                          alt={`Image ${index + 1}`}
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                        <Typography.Text
                          strong
                        >{`Image ${index + 1}`}</Typography.Text>
                      </Flex>
                      <Button size="small" href={url} target="_blank" download>
                        Open
                      </Button>
                    </Flex>
                  ))}
                </div>
              </>
            )}
          </>
        )
      case 'Markdown':
        return (
          <Typography.Paragraph>
            1. Paste this into any Markdown editor like Obsidian, Notion, or
            GitHub.
          </Typography.Paragraph>
        )
      case 'HTML':
        return (
          <Typography.Paragraph>
            1. Paste this into any raw HTML editor or web builder that supports
            HTML blocks.
          </Typography.Paragraph>
        )
      default:
        return null
    }
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={5}>{platform} Instructions</Typography.Title>
      {renderInstructions()}
      <Flex justify="end" gap={8}>
        <Button onClick={onBack}>Back</Button>
        <Button type="primary" onClick={handleCopy}>
          Copy for {platform}
        </Button>
      </Flex>
    </Space>
  )
}
